import clsx from "clsx"
import {
  createContext,
  forwardRef,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  useContext
} from "react"

import type { ThemeKeys } from "../../theme"
import { Icon, type IconName } from "../Icon"
import { Text, textVariantMapping } from "../Text"
import styles from "./ActionList.module.css"

/**
 * @internal
 * Action List context used to propagate the border variant
 * for all items in the Action List.
 */
type ActionListContext = { variant: ActionListVariants }

const ActionListContext = createContext<ActionListContext>({
  variant: "bordered"
})

function useActionList() {
  return useContext(ActionListContext)
}

export type ActionListVariants = "borderless" | "bordered"

export interface ActionListProps extends PropsWithChildren {
  variant?: ActionListVariants
  gap?: 0 | 8
}

export function ActionList(props: ActionListProps) {
  const { variant = "bordered", gap = 8 } = props
  return (
    <ActionListContext.Provider value={{ variant }}>
      <div
        {...props}
        className={clsx(
          styles.root,
          variant === "bordered" && styles.rootBorder,
          gap && styles[`gap-${gap}`]
        )}
      />
    </ActionListContext.Provider>
  )
}

type Color = "grey" | "tomato"
export interface ActionListItemProps {
  children: ReactNode
  color?: Color
  backgroundColor?: "grey" | "transparent"
  description?: ReactNode
  disabled?: boolean
  selected?: boolean
  endAdornment?: ReactNode
  endIcon?: IconName
  onClick?: (event: MouseEvent) => void
  renderEndIcon?: (icon: ReactNode) => ReactNode
  renderStartIcon?: (icon: ReactNode) => ReactNode
  startIcon?: IconName
  readOnly?: boolean
  textVariant?: keyof typeof textVariantMapping
  /**
   * For progress step use-case,
   * mandatory to pass the boolean value for each item, even if `false`.
   */
  completed?: boolean
}

export const ActionListItem = forwardRef<HTMLDivElement, ActionListItemProps>(
  function ActionListItem(props, ref) {
    const { variant } = useActionList()
    const {
      children,
      color = "grey",
      backgroundColor = "grey",
      description,
      disabled,
      selected = false,
      endAdornment,
      endIcon,
      renderEndIcon = (icon) => icon,
      renderStartIcon = (icon) => icon,
      startIcon,
      completed,
      readOnly = false,
      textVariant = variant === "borderless" ? "bold" : "smallBold",

      ...otherProps
    } = props
    const hasStartIcon = Boolean(props.renderStartIcon || props.startIcon)

    const textColor = mapColorToTheme[selected ? "purple" : color]

    return (
      <div
        className={clsx(
          styles.item,
          styles[`item-${color}`],
          styles[`item-bg-${backgroundColor}`],
          styles[`item-${variant}`],
          selected && styles.selected,
          disabled && styles.disabled,
          readOnly && styles.readOnly
        )}
        ref={ref}
        {...otherProps}
      >
        {typeof completed === "boolean" && <StepMarker completed={completed} />}

        {hasStartIcon && (
          <div
            className={clsx(
              styles.startIcon,
              variant === "bordered" && styles.iconBorder
            )}
          >
            {renderStartIcon(
              startIcon && <Icon name={startIcon} size="large" />
            )}
          </div>
        )}

        <div className={styles.iconText}>
          <Text component="span" color={textColor.name} variant={textVariant}>
            {children}
          </Text>

          <Text component="span" color={textColor.description} variant="small">
            {description}
          </Text>
        </div>

        {renderEndIcon(endIcon && <Icon name={endIcon} size="small" />)}

        {endAdornment}
      </div>
    )
  }
)

interface StepMarkerProps {
  completed: boolean
}

/**
 * @private
 */
function StepMarker(props: StepMarkerProps) {
  const { completed } = props

  return (
    <div className={styles.stepMarker}>
      <span
        className={clsx(styles.stepMarkerCircle, completed && styles.completed)}
        aria-label={completed ? "step completed" : "step not completed"}
      />
    </div>
  )
}

interface TextColors {
  name: ThemeKeys<"color">
  description: ThemeKeys<"color">
}

// Extend the Color type with "purple" for the selected item UI
type ExtendedColor = Color | "purple"

const mapColorToTheme: Record<ExtendedColor, TextColors> = {
  grey: { name: "grey12", description: "grey11" },
  tomato: { name: "tomato11", description: "tomato11" },
  purple: { name: "purple11", description: "purple11" }
}
